import { useSettlementToken } from "features/tokens/useSettlementToken";
import React, { useState } from "react";
import "./merchant-row.css";
import StatusBadge, {
  StatusVariant,
} from "components/status-badge/StatusBadge";
import Button from "components/button/Button";
import OverflowMenu, {
  OverflowMenuAction,
} from "components/overflow-menu/OverflowMenu";
import { useBlockMerchant } from "features/merchants/useBlockMerchant";
import { useMerchantStatusToggle } from "features/merchants/useMerchantStatusToggle";
import { useMerchantSettlement } from "features/merchants/useMerchantSettlement";
import { countryAlpha2FromMerchantId } from "utils/merchantId";
import {
  AngleDown,
  CopyIconV1,
  CheckCircleIcon,
  CoinsIconV1,
  BanIconV1,
  PauseCircleIconV1,
  PlayCircleIconV1,
} from "images/image-component";
import ApprovalsModal from "features/approvals/ApprovalsModal";
import LoadingModal from "modals/loading/LoadingModal";
import ErrorModal from "modals/error-modal/ErrorModal";
import SuccessModal from "modals/success-modal/SuccessModal";
import type { MerchantSettlementInfo } from "utils/AxiosUtil";

const PLATFORM_LABELS: Record<string, string> = {
  stripe: "Stripe",
  column: "Column",
};

// "Stripe - Sandbox" / "Column - Live". Mode test ⇒ Sandbox, live ⇒ Live.
function formatPlatform(info: MerchantSettlementInfo): string {
  if (!info.signedUp || !info.platformId) return "Not set up";
  const name = PLATFORM_LABELS[info.platformId] ?? info.platformId;
  return `${name} - ${info.settlementMode === "live" ? "Live" : "Sandbox"}`;
}

export interface MerchantRecord {
  merchantId: string;
  /** Single uppercase letter used as the logo */
  logoLetter: string;
  /** CSS gradient for logo background, e.g. "linear-gradient(135deg,#c07a4a,#8a5230)" */
  merchantName: string;
  merchantAddress: string;
  status: StatusVariant;
  merchantStatus: number;
  logoGradient?: string;
  category?: string;
  memberSince?: string;
  /** Shortened wallet address */
  /** Total VIP spent / volume */
  balance?: number;
  /** False until the merchant completes Firebase signup (add-merchant step 2). */
  signedUp?: boolean;
}

type MerchantRowProps = MerchantRecord & {
  /** Fired for a merchant that registered on-chain but hasn't finished signup. */
  onSignUp?: (merchantId: string) => void;
};

function MerchantRow({
  merchantId,
  logoLetter,
  logoGradient = "linear-gradient(135deg,#c07a4a,#8a5230)",
  merchantName,
  category,
  memberSince,
  merchantAddress,
  status,
  balance,
  signedUp,
  onSignUp,
}: MerchantRowProps) {
  const { label: selectedTokenLabel } = useSettlementToken();
  console.log(`MerchantRow status: `, status);
  const {
    blockMerchantError,
    blockMerchantErrorMsg,
    blockMerchantSuccess,
    blockMerchantSuccessMsg,
    blockingMerchant,
    blockingMerchantMsg,
    handleBlockMerchant,
    handleCloseError,
    handleCloseSuccess,
    handleRetry,
  } = useBlockMerchant();
  const {
    changingMerchantStatus,
    changingMerchantStatusMsg,
    merchantStatusError,
    merchantStatusErrorMsg,
    merchantStatusSuccess,
    merchantStatusSuccessMsg,
    handleMerchantStatusChange,
    handleRetry: handleMerchantStatusRetry,
    handleCloseError: handleCloseMerchantStatusError,
    handleCloseSuccess: handleCloseMerchantStatusSuccess,
  } = useMerchantStatusToggle();

  const [expanded, setExpanded] = useState(false);
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const ctaIsDisabled =
    status === "Revoked" ||
    status === "Manufactured" ||
    status === "Suspended" ||
    status === "Blocked" ||
    status === "Unassigned";

  // Platform + mode are fetched lazily, only once the detail is expanded.
  const {
    data: settlement,
    isLoading,
    isError,
  } = useMerchantSettlement(merchantId, expanded);

  const countryAlpha2 = countryAlpha2FromMerchantId(merchantId);

  const copy = (key: string, value: string) => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopiedKey(key);
      window.setTimeout(
        () => setCopiedKey((k) => (k === key ? null : k)),
        1200,
      );
    });
  };

  // Case-folded: StatusVariant permits other casings, though getMerchantStatus only ever
  // produces the capitalised forms.
  const normalizedStatus = status?.toLowerCase();
  const isSuspended = normalizedStatus === "suspended";
  const canToggleMerchantStatus = isSuspended || normalizedStatus === "active";

  const overflowActions: OverflowMenuAction[] = [
    {
      label: "Add currency",
      icon: <CoinsIconV1 size={16} />,
      onClick: () => setApprovalsOpen(true),
      disabled: ctaIsDisabled,
    },
    {
      label: isSuspended ? "Activate merchant" : "Suspend merchant",
      icon: isSuspended ? (
        <PlayCircleIconV1 size={16} />
      ) : (
        <PauseCircleIconV1 size={16} />
      ),
      onClick: () =>
        handleMerchantStatusChange(
          isSuspended ? "activate" : "suspend",
          merchantId,
        ),
      disabled: !canToggleMerchantStatus,
    },
    {
      label: "Block merchant",
      icon: <BanIconV1 size={16} color="var(--neg)" />,
      onClick: () => handleBlockMerchant(merchantId),
      disabled: status === "Blocked",
    },
  ];

  const renderCopy = (key: string, value: string) => (
    <button
      type="button"
      className="mr-copy-btn"
      onClick={() => copy(key, value)}
      aria-label={`Copy ${key}`}
    >
      {copiedKey === key ? (
        <CheckCircleIcon size={14} />
      ) : (
        <CopyIconV1 size={14} />
      )}
    </button>
  );

  return (
    <div className="mr-row">
      <div className="mr-id">
        <div
          className="mr-logo"
          style={{ background: logoGradient }}
          aria-hidden="true"
        >
          {logoLetter}
        </div>

        <div className="mr-info">
          <div className="mr-name">{merchantName}</div>
          {/* <div className="mr-meta">
            {category} · since {memberSince}
          </div> */}
        </div>
      </div>

      {/* <div className="mr-addr" title={address}>
        {address}
      </div> */}

      <div className="mr-total">
        {balance !== undefined ? (
          <>
            {balance.toFixed(2)}
            <small> {selectedTokenLabel}</small>
          </>
        ) : (
          <span className="mr-balance-skeleton" />
        )}
      </div>

      <span>
        <StatusBadge status={status} />
      </span>

      <div className="mr-actions">
        {!signedUp && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onSignUp?.(merchantId)}
          >
            Sign up
          </Button>
        )}
        <OverflowMenu
          actions={overflowActions}
          ariaLabel="More merchant actions"
        />
        <button
          type="button"
          className={`mr-chevron${expanded ? " mr-chevron--open" : ""}`}
          onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? "Hide details" : "Show details"}
          aria-expanded={expanded}
        >
          <AngleDown size={16} />
        </button>
      </div>

      {expanded && (
        <div className="mr-detail">
          <div className="mr-detail-item">
            <span className="mr-detail-k">Merchant ID</span>
            <span className="mr-detail-v">
              <span className="mr-mono">{merchantId}</span>
              {renderCopy("merchant id", merchantId)}
            </span>
          </div>
          <div className="mr-detail-item">
            <span className="mr-detail-k">Address</span>
            <span className="mr-detail-v">
              <span className="mr-mono" title={merchantAddress}>
                {merchantAddress}
              </span>
              {renderCopy("address", merchantAddress)}
            </span>
          </div>
          <div className="mr-detail-item">
            <span className="mr-detail-k">Country</span>
            <span className="mr-detail-v">{countryAlpha2 || "—"}</span>
          </div>
          <div className="mr-detail-item">
            <span className="mr-detail-k">Platform</span>
            <span className="mr-detail-v">
              {isError ? (
                "Unavailable"
              ) : isLoading || !settlement ? (
                <span className="mr-detail-skeleton" />
              ) : (
                formatPlatform(settlement)
              )}
            </span>
          </div>
        </div>
      )}

      {/* Mounted only while open: the audit runs on mount, so unmounting is the reset. */}
      {approvalsOpen && (
        <ApprovalsModal
          isOpen
          kind="merchant"
          id={merchantId}
          displayName={merchantName}
          onClose={() => setApprovalsOpen(false)}
        />
      )}

      {blockingMerchant && (
        <LoadingModal
          isOpen={blockingMerchant}
          loadingMsg={blockingMerchantMsg}
        />
      )}
      {blockMerchantError && (
        <ErrorModal
          isOpen={blockMerchantError}
          errorMsg={blockMerchantErrorMsg}
          handleClose={handleCloseError}
          tryAgain={handleRetry}
        />
      )}
      {blockMerchantSuccess && (
        <SuccessModal
          isOpen={blockMerchantSuccess}
          message={blockMerchantSuccessMsg}
          handleClose={handleCloseSuccess}
        />
      )}

      {changingMerchantStatus && (
        <LoadingModal
          isOpen={changingMerchantStatus}
          loadingMsg={changingMerchantStatusMsg}
        />
      )}
      {merchantStatusError && (
        <ErrorModal
          isOpen={merchantStatusError}
          errorMsg={merchantStatusErrorMsg}
          handleClose={handleCloseMerchantStatusError}
          tryAgain={handleMerchantStatusRetry}
        />
      )}
      {merchantStatusSuccess && (
        <SuccessModal
          isOpen={merchantStatusSuccess}
          message={merchantStatusSuccessMsg}
          handleClose={handleCloseMerchantStatusSuccess}
        />
      )}
    </div>
  );
}

export default MerchantRow;
